/**
 * ============================================================
 * KEJJA OS — src/lib/mealsApi.ts  (v1.1)
 * ============================================================
 */

import { api } from '@/lib/api';

// ============================================================
// Types
// ============================================================

export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';
export type ShoppingItemStatus = 'pending' | 'bought' | 'skipped';

export interface RecipeStep {
  title: string;
  body: string;
  timerSeconds?: number;
}

export interface Recipe {
  id: string;
  homeId: string;
  name: string;
  description: string | null;
  servings: number;
  prepMinutes: number | null;
  cookMinutes: number | null;
  tags: string[];
  steps: RecipeStep[];
  method: string | null;           // repurposed as "tips"
  icon: string | null;
  color: string | null;
  isFavorite: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface RecipeIngredient {
  id: string;
  recipeId: string;
  homeId: string;
  name: string;
  quantity: string | null;
  unit: string | null;
  note: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface RecipeWithIngredients extends Recipe {
  ingredients: RecipeIngredient[];
}

export interface MealPlanSlot {
  id: string;
  homeId: string;
  weekStartDate: string;
  dayOfWeek: number;
  mealType: MealType;
  recipeId: string | null;
  mealText: string | null;
  notes: string | null;
  servingsOverride: number | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShoppingListItem {
  id: string;
  homeId: string;
  name: string;
  quantity: string | null;
  category: string | null;
  notes: string | null;
  assigneeUserId: string | null;
  status: ShoppingItemStatus;
  boughtAt: string | null;
  boughtByUserId: string | null;
  source: string;
  sourceRef: string | null;
  sortOrder: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface MealsPulse {
  today: string;
  weekStartDate: string;
  plan: {
    plannedSlots: number;
    totalMainSlots: number;
    percentPlanned: number;
    todayMeals: Array<{
      mealType: MealType;
      label: string | null;
      recipeId: string | null;
    }>;
  };
  shopping: {
    pendingCount: number;
    pendingAssignedMe: number;
    pendingUnassigned: number;
    boughtThisWeek: number;
    topPending: Array<{
      id: string;
      name: string;
      quantity: string | null;
      assigneeUserId: string | null;
    }>;
  };
  recipes: { total: number; favorites: number };
  groceryFinance: {
    enabled: boolean;
    weekSpend: number;
    currencyCode: string;
    weekBudget: number | null;
    percentOfBudget: number | null;
  };
}

// ============================================================
// Payloads
// ============================================================

export interface RecipeCreatePayload {
  name: string;
  description?: string | null;
  servings?: number;
  prepMinutes?: number | null;
  cookMinutes?: number | null;
  tags?: string[];
  steps?: RecipeStep[];
  method?: string | null;
  icon?: string | null;
  color?: string | null;
  isFavorite?: boolean;
  ingredients?: Array<{
    name: string;
    quantity?: string | number | null;
    unit?: string | null;
    note?: string | null;
  }>;
}

export type RecipeUpdatePayload = Partial<RecipeCreatePayload>;

export interface SlotUpsertPayload {
  weekStartDate: string;
  dayOfWeek: number;
  mealType: MealType;
  recipeId?: string | null;
  mealText?: string | null;
  notes?: string | null;
  servingsOverride?: number | null;
}

export interface ShoppingCreatePayload {
  name: string;
  quantity?: string | null;
  category?: string | null;
  notes?: string | null;
  assigneeUserId?: string | null;
}

export type ShoppingUpdatePayload = Partial<ShoppingCreatePayload>;

// ============================================================
// API
// ============================================================


export interface ParsedRecipeDraft {
  name: string;
  description?: string | null;
  servings?: number;
  prepMinutes?: number | null;
  cookMinutes?: number | null;
  tags?: string[];
  ingredients: Array<{
    name: string;
    quantity?: number | null;
    unit?: string | null;
    note?: string | null;
  }>;
  steps: Array<{ title: string; body: string; timerSeconds?: number }>;
  method?: string | null;
  source?: {
    kind: 'url' | 'html' | 'text' | 'pdf';
    url?: string;
    jsonLd: boolean;
  };
}

interface MealsApi {
  pulse: (homeId: string) => Promise<MealsPulse>;

  listRecipes: (homeId: string, favoritesOnly?: boolean) => Promise<{ recipes: Recipe[] }>;
  getRecipe: (homeId: string, recipeId: string) => Promise<{ recipe: RecipeWithIngredients }>;
  createRecipe: (homeId: string, body: RecipeCreatePayload) => Promise<{ recipe: RecipeWithIngredients }>;
  updateRecipe: (homeId: string, recipeId: string, body: RecipeUpdatePayload) => Promise<{ recipe: RecipeWithIngredients }>;
  deleteRecipe: (homeId: string, recipeId: string) => Promise<void>;
  seedRecipes: (homeId: string, replace?: boolean) => Promise<{ seeded: number; replaced: boolean }>;
  parseRecipe: (homeId: string, input: { url?: string; text?: string; pdfBase64?: string }) => Promise<{ draft: ParsedRecipeDraft }>;

  getPlan: (homeId: string, weekStartDate?: string) => Promise<{ weekStartDate: string; slots: MealPlanSlot[] }>;
  upsertSlot: (homeId: string, body: SlotUpsertPayload) => Promise<{ slot: MealPlanSlot }>;
  clearSlot: (homeId: string, weekStartDate: string, dayOfWeek: number, mealType: MealType) => Promise<void>;
  generateList: (homeId: string, weekStartDate?: string) => Promise<{
    itemsCreated: number; ingredientsAggregated: number; skippedSlots: number;
  }>;

  listShopping: (homeId: string, includeBought?: boolean) => Promise<{ items: ShoppingListItem[] }>;
  createShopping: (homeId: string, body: ShoppingCreatePayload) => Promise<{ item: ShoppingListItem }>;
  updateShopping: (homeId: string, itemId: string, body: ShoppingUpdatePayload) => Promise<{ item: ShoppingListItem }>;
  markBought: (homeId: string, itemId: string) => Promise<{ item: ShoppingListItem }>;
  markPending: (homeId: string, itemId: string) => Promise<{ item: ShoppingListItem }>;
  markSkipped: (homeId: string, itemId: string) => Promise<{ item: ShoppingListItem }>;
  deleteShopping: (homeId: string, itemId: string) => Promise<void>;
  clearBought: (homeId: string) => Promise<{ cleared: number }>;
}

const base = (homeId: string) => `/homes/${homeId}/meals`;

export const mealsApi: MealsApi = {
  pulse: (homeId) => api.get<MealsPulse>(`${base(homeId)}/pulse`),

  listRecipes: (homeId, favoritesOnly = false) =>
    api.get<{ recipes: Recipe[] }>(
      `${base(homeId)}/recipes${favoritesOnly ? '?favoritesOnly=true' : ''}`,
    ),
  getRecipe: (homeId, recipeId) =>
    api.get<{ recipe: RecipeWithIngredients }>(`${base(homeId)}/recipes/${recipeId}`),
  createRecipe: (homeId, body) =>
    api.post<{ recipe: RecipeWithIngredients }>(`${base(homeId)}/recipes`, body),
  updateRecipe: (homeId, recipeId, body) =>
    api.patch<{ recipe: RecipeWithIngredients }>(`${base(homeId)}/recipes/${recipeId}`, body),
  deleteRecipe: (homeId, recipeId) =>
    api.delete<void>(`${base(homeId)}/recipes/${recipeId}`),
  seedRecipes: (homeId, replace = false) =>
    api.post<{ seeded: number; replaced: boolean }>(`${base(homeId)}/recipes/seed`, { replace }),
  parseRecipe: (homeId, input) =>
    api.post<{ draft: ParsedRecipeDraft }>(`${base(homeId)}/recipes/parse`, input),

  getPlan: (homeId, weekStartDate) =>
    api.get<{ weekStartDate: string; slots: MealPlanSlot[] }>(
      `${base(homeId)}/plan${weekStartDate ? `?week=${weekStartDate}` : ''}`,
    ),
  upsertSlot: (homeId, body) =>
    api.post<{ slot: MealPlanSlot }>(`${base(homeId)}/plan/slot`, body),
  clearSlot: (homeId, weekStartDate, dayOfWeek, mealType) =>
    api.delete<void>(
      `${base(homeId)}/plan/slot?weekStartDate=${weekStartDate}&dayOfWeek=${dayOfWeek}&mealType=${mealType}`,
    ),
  generateList: (homeId, weekStartDate) =>
    api.post<{ itemsCreated: number; ingredientsAggregated: number; skippedSlots: number }>(
      `${base(homeId)}/plan/generate-list`,
      weekStartDate ? { weekStartDate } : {},
    ),

  listShopping: (homeId, includeBought = false) =>
    api.get<{ items: ShoppingListItem[] }>(
      `${base(homeId)}/shopping${includeBought ? '?includeBought=true' : ''}`,
    ),
  createShopping: (homeId, body) =>
    api.post<{ item: ShoppingListItem }>(`${base(homeId)}/shopping`, body),
  updateShopping: (homeId, itemId, body) =>
    api.patch<{ item: ShoppingListItem }>(`${base(homeId)}/shopping/${itemId}`, body),
  markBought: (homeId, itemId) =>
    api.post<{ item: ShoppingListItem }>(`${base(homeId)}/shopping/${itemId}/buy`, {}),
  markPending: (homeId, itemId) =>
    api.post<{ item: ShoppingListItem }>(`${base(homeId)}/shopping/${itemId}/unbuy`, {}),
  markSkipped: (homeId, itemId) =>
    api.post<{ item: ShoppingListItem }>(`${base(homeId)}/shopping/${itemId}/skip`, {}),
  deleteShopping: (homeId, itemId) =>
    api.delete<void>(`${base(homeId)}/shopping/${itemId}`),
  clearBought: (homeId) =>
    api.post<{ cleared: number }>(`${base(homeId)}/shopping/clear-bought`, {}),
};
